import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import PageHeader from "../../utils/PageHeader";

import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StorageIcon from "@mui/icons-material/Storage";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import BreadcrumbsBar from "../../component/common/BreadcrumbsBar";
import WelcomeSection from "../../component/common/WelcomeSection";

import { monthNames } from "../../../src/component/constent";
import DashboardIcon from "@mui/icons-material/Dashboard";
import {
  currentMonth,
  getDashboardDataStatus,
} from "../../services/dashBoardService";

import SEO from "../../utils/SEO";

// ============================================================
// DATA CONFIGURATION
// ============================================================

const DATA_ITEMS = [
  {
    key: "inputdata_immis",
    title: "Store Data",
    subtitle: "iMMIS",
    icon: StorageIcon,
  },
  {
    key: "labour_timeoffice",
    title: "Time Office",
    subtitle: "Labour Data",
    icon: CalendarMonthIcon,
  },
  {
    key: "labour_insentive",
    title: "Incentive",
    subtitle: "Labour Data",
    icon: TrendingUpIcon,
  },
  {
    key: "inputdata_ioh",
    title: "IOH Data",
    subtitle: "Input Data",
    icon: DataUsageIcon,
  },
  {
    key: "inputdata_poh",
    title: "POH Data",
    subtitle: "Input Data",
    icon: DataUsageIcon,
  },
  {
    key: "inputdata_wls",
    title: "WLS Data",
    subtitle: "Input Data",
    icon: StorageIcon,
  },
  {
    key: "enga_section_data",
    title: "EGA / ENGA",
    subtitle: "Section Data",
    icon: DataUsageIcon,
  },
  {
    key: "inputdata_creditjv",
    title: "Credit JV",
    subtitle: "Input Data",
    icon: StorageIcon,
  },
  {
    key: "freightandproformaoncost",
    title: "Store Proforma",
    subtitle: "Freight & Proforma",
    icon: StorageIcon,
  },
];

// ============================================================
// DASHBOARD
// ============================================================

export default function Dashboard() {
  const [monthData, setMonthData] = useState(null);

  const [status, setStatus] = useState(null);

  const [loadingMonth, setLoadingMonth] = useState(true);

  const [loadingStatus, setLoadingStatus] = useState(false);

  const [refreshing, setRefreshing] = useState(false);


const [selectedMonth, setSelectedMonth] = useState(null);
const [selectedYear, setSelectedYear] = useState(null);



// ========================================================
// AVAILABLE MONTHS
// Current month + previous 6 months
// Handles January / December year changes automatically
// ========================================================

const availableMonths = useMemo(() => {
  if (!monthData?.data?.month || !monthData?.data?.year) {
    return [];
  }

  const currentMonth = Number(monthData.data.month);
  const currentYear = Number(monthData.data.year);

  return Array.from({ length: 7 }, (_, index) => {
    // Convert current month/year into a single month index.
    // This automatically handles January -> December previous year.
    const date = new Date(
      currentYear,
      currentMonth - 1 - index,
      1
    );

    return {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  });
}, [monthData]);










  // ========================================================
  // LOAD CURRENT MONTH
  // ========================================================

  const loadCurrentMonth = async () => {
    try {
      setLoadingMonth(true);

      const res = await currentMonth();

      setMonthData(res.data);


if (!selectedMonth || !selectedYear) {
  setSelectedMonth(res.data?.data?.month);
  setSelectedYear(res.data?.data?.year);
}

      
    } catch (err) {
      console.error("Current month error:", err);
    } finally {
      setLoadingMonth(false);
    }
  };

  // ========================================================
  // LOAD DATA STATUS
  // ========================================================

 const loadStatus = async () => {
  try {
    const month = selectedMonth;
    const year = selectedYear;

    if (!month || !year) {
      return;
    }

    setLoadingStatus(true);

    const res = await getDashboardDataStatus(month, year);

    setStatus(res.data);
  } catch (err) {
    console.error("Data status error:", err);
  } finally {
    setLoadingStatus(false);
  }
};

  // ========================================================
  // INITIAL LOAD
  // ========================================================

  useEffect(() => {
    loadCurrentMonth();
  }, []);

  // ========================================================
  // LOAD STATUS AFTER MONTH
  // ========================================================

  useEffect(() => {
  if (selectedMonth && selectedYear) {
    loadStatus();
  }
}, [selectedMonth, selectedYear]);
  // ========================================================
  // REFRESH
  // ========================================================

  const handleRefresh = async () => {
    try {
      setRefreshing(true);

      await loadCurrentMonth();
    } finally {
      setRefreshing(false);
    }
  };

  // ========================================================
  // MONTH / YEAR
  // ========================================================

  const month = selectedMonth;
const year = selectedYear;

const monthName = month ? monthNames[month] : "";

  // ========================================================
  // SUMMARY
  // ========================================================

  const summary = useMemo(() => {
    if (!status?.data) {
      return {
        total: DATA_ITEMS.length,
        completed: 0,
        pending: DATA_ITEMS.length,
        percentage: 0,
        totalRows: 0,
      };
    }

    let completed = 0;

    let totalRows = 0;

    DATA_ITEMS.forEach((item) => {
      const itemStatus = status.data[item.key];

      if (itemStatus?.status) {
        completed++;
      }

      totalRows += Number(itemStatus?.rowCount || 0);
    });

    const pending = DATA_ITEMS.length - completed;

    const percentage =
      DATA_ITEMS.length > 0
        ? Math.round((completed / DATA_ITEMS.length) * 100)
        : 0;

    return {
      total: DATA_ITEMS.length,
      completed,
      pending,
      percentage,
      totalRows,
    };
  }, [status]);

  // ========================================================
  // STATUS CARD
  // ========================================================

  // const StatusCard = ({ item }) => {
  //   const Icon = item.icon || StorageIcon;

  //   const data = status?.data?.[item.key];

  //   const rowCount = Number(data?.rowCount || 0);

  //   const completed = Boolean(data?.status);

  //   return (
  //     <Paper
  //       elevation={0}
  //       sx={{
  //         height: "100%",
  //         minHeight: 175,
  //         borderRadius: 3,
  //         overflow: "hidden",

  //         border: "1px solid",

  //         borderColor: completed ? "#b7dfb9" : "#efb5b5",

  //         background: completed
  //           ? "linear-gradient(145deg,#eaf7ec 0%,#ffffff 75%)"
  //           : "linear-gradient(145deg,#fff4f4 0%,#ffffff 75%)",

  //         transition: "all .25s ease",

  //         "&:hover": {
  //           transform: "translateY(-4px)",

  //           boxShadow: "0 12px 28px rgba(0,0,0,.10)",
  //         },
  //       }}
  //     >
  //       {/* STATUS STRIP */}

  //       <Box
  //         sx={{
  //           height: 5,

  //           backgroundColor: completed ? "#2e7d32" : "#d32f2f",
  //         }}
  //       />

  //       <Box
  //         sx={{
  //           p: 2,
  //         }}
  //       >
  //         {/* CARD HEADER */}

  //         <Stack
  //           direction="row"
  //           justifyContent="space-between"
  //           alignItems="flex-start"
  //         >
  //           <Stack direction="row" spacing={1.2} alignItems="center">
  //             <Box
  //               sx={{
  //                 width: 44,
  //                 height: 44,
  //                 borderRadius: 2,
  //                 display: "flex",
  //                 alignItems: "center",
  //                 justifyContent: "center",

  //                 backgroundColor: completed ? "#c8e6c9" : "#ffcdd2",

  //                 color: completed ? "#2e7d32" : "#c62828",
  //               }}
  //             >
  //               <Icon fontSize="small" />
  //             </Box>

  //             <Box>
  //               <Typography
  //                 sx={{
  //                   fontWeight: 800,
  //                   fontSize: "0.95rem",
  //                   lineHeight: 1.2,
  //                 }}
  //               >
  //                 {item.title}
  //               </Typography>

  //               <Typography variant="caption" color="text.secondary">
  //                 {item.subtitle}
  //               </Typography>
  //             </Box>
  //           </Stack>

  //           {/* STATUS */}

  //           {completed ? (
  //             <Tooltip title="Data available">
  //               <CheckCircleIcon
  //                 sx={{
  //                   color: "#2e7d32",
  //                   fontSize: 26,
  //                 }}
  //               />
  //             </Tooltip>
  //           ) : (
  //             <Tooltip title="Data not available">
  //               <CancelIcon
  //                 sx={{
  //                   color: "#d32f2f",
  //                   fontSize: 26,
  //                 }}
  //               />
  //             </Tooltip>
  //           )}
  //         </Stack>

  //         {/* ROW COUNT */}

  //         <Box
  //           sx={{
  //             mt: 2.5,
  //           }}
  //         >
  //           <Typography
  //             sx={{
  //               fontSize: "1.8rem",
  //               lineHeight: 1,
  //               fontWeight: 900,
  //             }}
  //           >
  //             {rowCount.toLocaleString()}
  //           </Typography>

  //           <Typography variant="caption" color="text.secondary">
  //             records available
  //           </Typography>
  //         </Box>

  //         {/* STATUS CHIP */}

  //         <Chip
  //           size="small"
  //           icon={completed ? <CheckCircleIcon /> : <CancelIcon />}
  //           label={completed ? "Data Available" : "No Data"}
  //           sx={{
  //             mt: 1.5,

  //             fontWeight: 700,

  //             backgroundColor: completed ? "#e8f5e9" : "#ffebee",

  //             color: completed ? "#2e7d32" : "#c62828",
  //           }}
  //         />
  //       </Box>
  //     </Paper>
  //   );
  // };




const StatusCard = ({ item }) => {
  const Icon = item.icon || StorageIcon;

  const data = status?.data?.[item.key];

  const rowCount = Number(data?.rowCount || 0);

  const completed = Boolean(data?.status);

  const lastEntryUser = data?.lastEntryUser || "-";
  const lastEntryTime = data?.lastEntryTime || null;






  // Format date/time nicely
  const formattedEntryTime = lastEntryTime
    ? new Date(lastEntryTime).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        minHeight: 205,
        borderRadius: 3,
        overflow: "hidden",

        border: "1px solid",

        borderColor: completed ? "#b7dfb9" : "#efb5b5",

        background: completed
          ? "linear-gradient(145deg,#eaf7ec 0%,#ffffff 75%)"
          : "linear-gradient(145deg,#fff4f4 0%,#ffffff 75%)",

        transition: "all .25s ease",

        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 28px rgba(0,0,0,.10)",
        },
      }}
    >
      {/* STATUS STRIP */}

      <Box
        sx={{
          height: 5,
          backgroundColor: completed ? "#2e7d32" : "#d32f2f",
        }}
      />

      <Box sx={{ p: 2 }}>
        {/* CARD HEADER */}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                backgroundColor: completed ? "#c8e6c9" : "#ffcdd2",

                color: completed ? "#2e7d32" : "#c62828",
              }}
            >
              <Icon fontSize="small" />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.95rem",
                  lineHeight: 1.2,
                }}
              >
                {item.title}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {item.subtitle}
              </Typography>
            </Box>
          </Stack>

          {/* STATUS */}

          {completed ? (
            <Tooltip title="Data available">
              <CheckCircleIcon
                sx={{
                  color: "#2e7d32",
                  fontSize: 26,
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Data not available">
              <CancelIcon
                sx={{
                  color: "#d32f2f",
                  fontSize: 26,
                }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* ROW COUNT */}

        <Box sx={{ mt: 2.2 }}>
          <Typography
            sx={{
              fontSize: "1.8rem",
              lineHeight: 1,
              fontWeight: 900,
            }}
          >
            {rowCount.toLocaleString()}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            records available
          </Typography>
        </Box>

        {/* LAST ENTRY INFORMATION */}

        {completed && (
          <Box
            sx={{
              mt: 1.8,
              pt: 1.3,
              borderTop: "1px solid rgba(0,0,0,.08)",
            }}
          >
            <Stack spacing={0.5}>
              {/* USER */}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Last Entry By
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "#1565c0",
                    textAlign: "right",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={lastEntryUser}
                >
                  {lastEntryUser}
                </Typography>
              </Stack>

              {/* DATE/TIME */}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Last Entry
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#555",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formattedEntryTime}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        )}

        {/* STATUS CHIP */}

        <Chip
          size="small"
          icon={completed ? <CheckCircleIcon /> : <CancelIcon />}
          label={completed ? "Data Available" : "No Data"}
          sx={{
            mt: 1.5,

            fontWeight: 700,

            backgroundColor: completed ? "#e8f5e9" : "#ffebee",

            color: completed ? "#2e7d32" : "#c62828",
          }}
        />
      </Box>
    </Paper>
  );
};


  // ========================================================
  // PROCESSING DATA
  // ========================================================

  const processingData = status?.data?.labour_sub_ledger;

  const processingRows = Number(processingData?.rowCount || 0);

  const processingCompleted = Boolean(processingData?.status);

  // ========================================================
  // RENDER
  // ========================================================

  return (
    <>
      <SEO
        title="WGR Data Management System"
        description="Mechanical Workshop, North Eastern Railway, Gorakhpur."
        url="https://wgr.mwsgkp.in/"
      />

      {/* <BreadcrumbsBar page="Dashboard" /> */}

      {/* <WelcomeSection /> */}

      {/* =================================================
                DASHBOARD HERO
            ================================================= */}

      <PageHeader
        title={`${monthName} - ${year} Data Dashboard`}
        subtitle="Data entry status for the selected month"
        icon={DashboardIcon}
        //statusLabel="Import Format"
        //statusValue="CSV File"
        //   statusChip={{
        //     label: loading
        //       ? "Importing..."
        //       : file
        //       ? "File Selected"
        //       : "Ready",
        //     color: loading
        //       ? "warning"
        //       : file
        //       ? "primary"
        //       : "success",
        //   }}
        showRefresh
         onRefresh={handleRefresh}
        // refreshing={loading}
      />

      {/* =================================================
                KPI SUMMARY
            ================================================= */}

      <Grid
        container
        spacing={2}
        sx={{
          mb: 3,
        }}
      >
        {/* TOTAL MODULES */}

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <KpiCard
            title="Total Modules"
            value={summary.total}
            subtitle="Configured data modules"
            icon={<StorageIcon />}
            background="#f8fbff"
            iconBackground="#e3f2fd"
            iconColor="#1565c0"
          />
        </Grid>

        {/* COMPLETED */}

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <KpiCard
            title="Completed/Work in Progress"
            value={summary.completed}
            subtitle="Modules with data"
            icon={<CheckCircleIcon />}
            background="#f5fbf6"
            iconBackground="#c8e6c9"
            iconColor="#2e7d32"
          />
        </Grid>

        {/* PENDING */}

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <KpiCard
            title="Pending"
            value={summary.pending}
            subtitle="Modules without data"
            icon={<CancelIcon />}
            background="#fff8f8"
            iconBackground="#ffcdd2"
            iconColor="#c62828"
          />
        </Grid>

        {/* TOTAL ROWS */}

        {/* <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <KpiCard
            title="Total Records"
            value={summary.totalRows.toLocaleString()}
            subtitle="Records entered"
            icon={<DataUsageIcon />}
            background="#f7fbff"
            iconBackground="#bbdefb"
            iconColor="#1565c0"
          />
        </Grid> */}
      </Grid>

      {/* =================================================
                OVERALL PROGRESS
            ================================================= */}

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: {
            xs: 2,
            sm: 2.5,
            md: 3,
          },

          borderRadius: 3,

          border: "1px solid #e0e0e0",

          backgroundColor: "#ffffff",
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
          sx={{
            mb: 1.5,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
              }}
            >
              Overall Data Entry Progress
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {summary.completed} of {summary.total} modules completed
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: "1.35rem",
              fontWeight: 900,
              color: "#1976d2",
            }}
          >
            {summary.percentage}%
          </Typography>
        </Stack>

        {/* PROGRESS BAR */}

        <Box
          sx={{
            height: 14,
            borderRadius: 20,
            overflow: "hidden",
            backgroundColor: "#e3f2fd",
          }}
        >
          <Box
            sx={{
              width: `${summary.percentage}%`,

              height: "100%",

              borderRadius: 20,

              background: "linear-gradient(90deg,#1565c0,#42a5f5)",

              transition: "width .5s ease",
            }}
          />
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Data entry completion
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {summary.pending} pending
          </Typography>
        </Stack>
      </Paper>

      {/* =================================================
                DATA ENTRY STATUS
            ================================================= */}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #dfe4ea",
          backgroundColor: "#ffffff",
          overflow: "hidden",
        }}
      >
        {/* SECTION HEADER */}

        <Box
          sx={{
            px: {
              xs: 2,
              sm: 2.5,
              md: 3,
            },
            py: 2,

            background: "linear-gradient(90deg,#f5f9ff,#ffffff)",

            borderBottom: "1px solid #e5e9ef",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: "#1565c0",
                  fontSize: "1.1rem",
                }}
              >
                Data Entry Status
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Monitor all monthly data modules
              </Typography>
            </Box>

            {/* <Chip
              icon={<CalendarMonthIcon />}
              label={monthName ? `${monthName} ${year}` : "Current Month"}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 700,
              }}
            /> */}


<FormControl size="small" sx={{ minWidth: 180 }}>
  <InputLabel>Month</InputLabel>

  <Select
    value={`${selectedMonth}-${selectedYear}`}
    label="Month"
    onChange={(e) => {
      const [m, y] = e.target.value.split("-");
      setSelectedMonth(Number(m));
      setSelectedYear(Number(y));
    }}
  >
    {availableMonths.map((item) => (
      <MenuItem
        key={`${item.month}-${item.year}`}
        value={`${item.month}-${item.year}`}
      >
        {monthNames[item.month]} {item.year}
      </MenuItem>
    ))}
  </Select>
</FormControl>


            
          </Stack>
        </Box>

        {/* STATUS GRID */}

        {loadingStatus ? (
          <Box
            sx={{
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <CircularProgress />

            <Typography variant="body2" color="text.secondary">
              Loading data status...
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              p: {
                xs: 1.5,
                sm: 2,
                md: 2.5,
              },
            }}
          >
            <Grid container spacing={2}>
              {DATA_ITEMS.map((item) => (
                <Grid
                  key={item.key}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                    lg: 3,
                    xl: 2.4,
                  }}
                >
                  <StatusCard item={item} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* =================================================
                DATA PROCESSING STATUS
            ================================================= */}

      <Box
        sx={{
          mt: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",

            border: "1px solid",

            borderColor: processingCompleted ? "#a5d6a7" : "#ffe082",

            background: processingCompleted
              ? "linear-gradient(135deg,#edf8ee,#ffffff)"
              : "linear-gradient(135deg,#fffaf0,#ffffff)",

            boxShadow: "0 5px 18px rgba(0,0,0,.05)",
          }}
        >
          {/* PROCESSING HEADER */}

          <Box
            sx={{
              p: {
                xs: 2,
                sm: 2.5,
                md: 3,
              },
            }}
          >
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{
                xs: "flex-start",
                sm: "center",
              }}
            >
              {/* LEFT */}

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2.2,

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center",

                    backgroundColor: processingCompleted
                      ? "#c8e6c9"
                      : "#ffecb3",

                    color: processingCompleted ? "#2e7d32" : "#ef6c00",
                  }}
                >
                  <PlayArrowIcon />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: "1.05rem",
                    }}
                  >
                    Data Processing Status
                  </Typography>

                  {/* <Typography variant="body2" color="text.secondary">
                    Labour Sub Ledger
                  </Typography> */}
                </Box>
              </Stack>

              {/* RIGHT */}

              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={2}
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
              >
                {/* <Box>
                  <Typography
                    sx={{
                      fontSize: "1.7rem",
                      fontWeight: 900,
                      lineHeight: 1,
                    }}
                  >
                    {processingRows.toLocaleString()}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    processed rows
                  </Typography>
                </Box> */}

                <Chip
                  icon={
                    processingCompleted ? <CheckCircleIcon /> : <CancelIcon />
                  }
                  label={processingCompleted ? "Processed" : "Pending"}
                  sx={{
                    fontWeight: 800,

                    backgroundColor: processingCompleted
                      ? "#c8e6c9"
                      : "#ffecb3",

                    color: processingCompleted ? "#2e7d32" : "#ef6c00",
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          {/* PROCESSING FOOTER */}

          <Box
            sx={{
              px: {
                xs: 2,
                sm: 2.5,
                md: 3,
              },
              py: 1.3,

              borderTop: "1px solid",

              borderColor: processingCompleted ? "#d5ead6" : "#ffedb5",

              backgroundColor: "rgba(255,255,255,.55)",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {processingCompleted
                ? "Data processing has been completed for the selected month."
                : "Data processing is pending for the selected month."}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </>
  );
}

// ============================================================
// KPI CARD
// ============================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
  background,
  iconBackground,
  iconColor,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: 2.2,
        borderRadius: 3,

        border: "1px solid #e1e5ea",

        background: background || "#ffffff",

        transition: "all .25s ease",

        "&:hover": {
          transform: "translateY(-3px)",

          boxShadow: "0 10px 24px rgba(0,0,0,.08)",
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            backgroundColor: iconBackground,

            color: iconColor,

            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              lineHeight: 1.1,
              mt: 0.3,
              color: iconColor || "#222",
            }}
          >
            {value}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
